import { useAtomValue } from 'jotai';

import type {
  TBenchmarkingResponse,
  TTreatmentType,
} from '@/react-query/insights/types';

import { isCountsAtom } from '../../atoms';
import { useGetFilteredClinicalData } from '../data/getClinicalData';

import { calculateRates } from './calculateRates';

/*
Get counts per provider.
 */
export const useProviderCounts = (data?: TBenchmarkingResponse) => {
  const isCounts = useAtomValue(isCountsAtom);

  const providerData = data?.practices
    ?.flatMap((practice) => {
      const treatmentCosts = practice?.estimatedTreatmentValues;
      return practice?.providers?.map((provider) => {
        const initRecommendations: {
          viewedInVidea: number;
          planned: number;
          scheduled: number;
          matched: number;
          total: number;
          completed: number;
          unplanned: number;
        } = {
          viewedInVidea: 0,
          planned: 0,
          scheduled: 0,
          matched: 0,
          total: 0,
          completed: 0,
          unplanned: 0,
        };
        const allFilteredRecommendations = Object.entries(
          provider?.recommendations ?? {},
        );

        const recommendations = allFilteredRecommendations.reduce(
          (acc, [reco, metrics]) => {
            const treatmentCost = !isCounts
              ? (treatmentCosts?.[reco as TTreatmentType]?.value ?? 1)
              : 1;
            acc.viewedInVidea +=
              (metrics?.current?.total?.viewedInVidea ?? 0) * treatmentCost;
            acc.planned +=
              (metrics?.current?.planned?.treatmentCount ?? 0) * treatmentCost;
            acc.scheduled +=
              (metrics?.current?.scheduled?.treatmentCount ?? 0) *
              treatmentCost;
            acc.matched +=
              ((metrics?.current?.completed?.matchedTreatmentCount ?? 0) +
                (metrics?.current?.scheduled?.matchedTreatmentCount ?? 0) +
                (metrics?.current?.planned?.matchedTreatmentCount ?? 0)) *
              treatmentCost;
            acc.total +=
              (metrics?.current?.total?.treatmentCount ?? 0) * treatmentCost;
            acc.completed +=
              (metrics?.current?.completed?.treatmentCount ?? 0) *
              treatmentCost;
            acc.unplanned +=
              (metrics?.current?.unplanned?.treatmentCount ?? 0) *
              treatmentCost;
            return acc;
          },
          initRecommendations,
        );
        const initPreviousRecommendations: {
          viewedInVidea: number;
          planned: number;
          scheduled: number;
          matched: number;
          total: number;
          completed: number;
          unplanned: number;
        } = {
          viewedInVidea: 0,
          planned: 0,
          scheduled: 0,
          matched: 0,
          total: 0,
          completed: 0,
          unplanned: 0,
        };

        const previousRecommendations = allFilteredRecommendations.reduce(
          (acc, [reco, metrics]) => {
            const treatmentCost = !isCounts
              ? (treatmentCosts?.[reco as TTreatmentType]?.value ?? 1)
              : 1;
            acc.viewedInVidea +=
              (metrics?.previous?.total?.viewedInVidea ?? 0) * treatmentCost;
            acc.planned +=
              (metrics?.previous?.planned?.treatmentCount ?? 0) * treatmentCost;
            acc.scheduled +=
              (metrics?.previous?.scheduled?.treatmentCount ?? 0) *
              treatmentCost;
            acc.matched +=
              ((metrics?.previous?.completed?.matchedTreatmentCount ?? 0) +
                (metrics?.previous?.scheduled?.matchedTreatmentCount ?? 0) +
                (metrics?.previous?.planned?.matchedTreatmentCount ?? 0)) *
              treatmentCost;
            acc.total +=
              (metrics?.previous?.total?.treatmentCount ?? 0) * treatmentCost;
            acc.completed +=
              (metrics?.previous?.completed?.treatmentCount ?? 0) *
              treatmentCost;
            acc.unplanned +=
              (metrics?.previous?.unplanned?.treatmentCount ?? 0) *
              treatmentCost;
            return acc;
          },
          initPreviousRecommendations,
        );

        const initCompletedProduction: {
          current: number;
          previous: number;
        } = {
          current: 0,
          previous: 0,
        };

        const completedProduction = Object.entries(
          provider?.production ?? {},
        ).reduce((acc, [, metrics]) => {
          acc.current += isCounts
            ? (metrics?.current?.count ?? 0)
            : (metrics?.current?.value ?? 0);
          acc.previous += isCounts
            ? (metrics?.previous?.count ?? 0)
            : (metrics?.previous?.value ?? 0);
          return acc;
        }, initCompletedProduction);

        return {
          recommendations,
          previousRecommendations,
          completedProduction: completedProduction.current,
          previousCompletedProduction: completedProduction.previous,
          providerId:
            provider.providerVideaId ?? provider.providerId.toString(), // fallback because videa id is not available for mocked dev/sales demo data
          providerName: provider.providerName,
          providerRole: provider.providerRole,
          practiceId: practice.practiceVideaId,
          practiceName: practice.practiceName,
        };
      });
    })
    ?.filter(
      (provider) =>
        provider?.completedProduction || provider?.recommendations?.total,
    );

  return providerData;
};

/*
Get counts per provider and then calculate the rates.
* viewed in Videa = total viewed in Videa / total
* planned = (planned + scheduled + completed) / total
* acceptance = (scheduled + completed) / (planned + scheduled + completed)
* matched = (planned matched + scheduled matched + completed matched) / (planned + scheduled + completed)
 */
export const useProviderRates = ({
  practices,
  isDataFiltered,
}: {
  practices?: string[];
  isDataFiltered?: boolean;
}) => {
  const { data, isLoading } = useGetFilteredClinicalData({
    isPracticesEnabled: !!isDataFiltered,
    isProviderRoleEnabled: !!isDataFiltered,
  });

  const providerCounts = useProviderCounts(data)?.filter((provider) =>
    practices
      ? provider?.practiceId && practices.includes(provider?.practiceId)
      : true,
  );

  const providerRates = providerCounts
    ?.filter((provider) => provider !== undefined)
    .map((provider) => {
      return {
        providerId: provider.providerId,
        providerName: provider?.providerName,
        providerRole: provider?.providerRole,
        practiceId: provider?.practiceId,
        practiceName: provider?.practiceName,
        ...calculateRates(provider),
      };
    });

  return { providerRates, isLoading };
};
